<template>
  <div class="interview-bucket-list">
    <div v-if="interviews.length === 0" class="text-muted text-center py-2">
      No interviews found
    </div>
    <div v-else class="interview-list">
      <div v-for="interview in interviews" :key="`${interview.organizationReportId}-${interview.registrationId}`" class="interview-item">
        <div class="d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center">
            <span @click="onStarClick(interview)" class="star-icon" v-if="interview.organizationReportId">
              <fa class="gold-star" :icon="['fas', 'star']" fixed-width v-if="interview.isStarred" />
              <fa :icon="['far', 'star']" fixed-width v-if="!interview.isStarred" />
            </span>
            <a href="#" @click.prevent="viewReport(interview)" class="interview-link ml-2">
              {{ interview.registrationId }}
            </a>
          </div>
          <div class="interview-metrics">
            <span v-if="interview.observationCount" class="metric-badge metric-badge-warning" :title="`${interview.observationCount} observations`">
              <fa icon="exclamation-triangle" class="metric-icon" />
              {{ interview.observationCount }}
            </span>
            <span v-if="interview.admissionCount" class="metric-badge metric-badge-danger" :title="`${interview.admissionCount} admissions`">
              <fa icon="exclamation" class="metric-icon" />
              {{ interview.admissionCount }}
            </span>
            <span v-if="interview.skipTimeoutCount" class="metric-badge metric-badge-info" :title="`${interview.skipTimeoutCount} skips/timeouts`">
              <fa icon="info-circle" class="metric-icon" />
              {{ interview.skipTimeoutCount }}
            </span>
            <span
              v-if="interview.willingnessToReportCount"
              class="metric-badge metric-badge-warning"
              :title="`Total count: ${interview.willingnessToReportCount}`"
            >
              <fa icon="info-circle" class="metric-icon" />
              {{ interview.willingnessToReportCount }}
            </span>
            <span v-if="interview.totalCount" class="metric-badge metric-badge-secondary" :title="`Total count: ${interview.totalCount}`">
              <fa icon="info-circle" class="metric-icon" />
              {{ interview.totalCount }}
            </span>
          </div>
        </div>
        <!-- Category breakdown for skip/timeout counts -->
        <div v-if="getSortedCategoryBreakdown(interview).length > 0" class="category-breakdown">
          <div v-for="category in getSortedCategoryBreakdown(interview)" :key="category.categoryGroupCode" class="category-item">
            <span class="category-name">{{ category.categoryDisplayName }}</span>
            <span class="category-count">{{ category.skipTimeoutCount }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import formatter from "@/utils/formatter";

export default {
  props: {
    interviews: {
      type: Array,
      default: () => [],
    },
    organizationId: {
      type: String,
      required: true,
    },
  },
  computed: {
    formatter: () => formatter,
  },
  data() {
    return {
      error: null,
    };
  },
  methods: {
    viewReport(interview) {
      const baseRoute = `/organizations/${this.organizationId}/reports/${interview.organizationReportId}`;
      let routeData;
      if (interview.observationCount) {
        routeData = this.$router.resolve(`${baseRoute}?tab=observations`);
      } else {
        routeData = this.$router.resolve(baseRoute);
      }

      window.open(routeData.href, "_blank");
    },
    onStarClick(interview) {
      if (!interview || !interview.organizationReportId) return;

      // flip the starred value
      interview.isStarred = !interview.isStarred;

      // save to server
      this.$axios
        .put(`/v1/organizations/${this.organizationId}/reports/${interview.organizationReportId}/star/${interview.isStarred.toString()}`)
        .then(() => {
          // emit the star update event to synchronize with other components
          this.$emit("star-updated", {
            organizationReportId: interview.organizationReportId,
            isStarred: interview.isStarred,
            registrationId: interview.registrationId,
          });
        })
        .catch(err => {
          this.error = err;
          // revert on error
          interview.isStarred = !interview.isStarred;
        });
    },
    getSortedCategoryBreakdown(interview) {
      if (!interview || !interview.categoryBreakdown || !Array.isArray(interview.categoryBreakdown)) {
        return [];
      }
      // Sort by skipTimeoutCount descending (largest to smallest)
      return interview.categoryBreakdown.filter(category => category && category.skipTimeoutCount > 0).sort((a, b) => b.skipTimeoutCount - a.skipTimeoutCount);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/base.scss";

.interview-bucket-list {
  min-height: 200px;
  max-height: 400px; // Allow expansion but limit maximum height
  overflow-y: auto;
  border: 1px solid #e9ecef;
  border-radius: 0.375rem;
  background-color: #fff;
}

.interview-list {
  height: 100%;
}

.interview-item {
  min-height: 40px;
  padding: 8px 16px;
  border-bottom: 1px solid #f1f3f4;
  transition: background-color 0.15s ease-in-out;

  &:hover {
    background-color: #f8f9fa;
  }

  &:last-child {
    border-bottom: none;
  }
}

.interview-link {
  color: #0d6efd;
  text-decoration: none;
  font-weight: 500;
  font-size: 0.9rem;

  &:hover {
    color: #0a58ca;
    text-decoration: underline;
  }
}

.interview-metrics {
  display: flex;
  gap: 6px;
  align-items: center;
}

.metric-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.2;

  &.metric-badge-warning {
    background-color: #fff3cd;
    color: #856404;
    border: 1px solid #ffeaa7;
  }

  &.metric-badge-danger {
    background-color: #f8d7da;
    color: #721c24;
    border: 1px solid #f1aeb5;
  }

  &.metric-badge-info {
    background-color: #d1ecf1;
    color: #0c5460;
    border: 1px solid #bee5eb;
  }

  &.metric-badge-secondary {
    background-color: #e2e3e5;
    color: #383d41;
    border: 1px solid #d6d8db;
  }
}

.text-muted {
  font-size: 0.9rem;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.star-icon {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
}

.gold-star {
  color: #ffd700;
}

.metric-icon {
  margin-right: 6px;
}

.category-breakdown {
  margin-top: 6px;
  margin-left: 36px; // Increase indentation for better visual hierarchy
  padding-top: 4px;
  border-top: 1px solid #f1f3f4;
}

.category-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2px 0;
  font-size: 0.8rem;
  color: #6c757d;

  &:not(:last-child) {
    margin-bottom: 2px;
  }
}

.category-name {
  flex: 1;
  margin-right: 8px;
  font-weight: 500;
}

.category-count {
  flex-shrink: 0;
  background-color: #e9ecef;
  color: #495057;
  padding: 1px 6px;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 600;
  min-width: 20px;
  text-align: center;
}
</style>
